import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../context/ThemeContext';
import { translations } from '../constants/languages';

export default function Navbar({ onMenuClick, language, setLanguage, user, onAuthClick }) {
    const { isDark, setIsDark } = useTheme();
    const [isPlayMenuOpen, setIsPlayMenuOpen] = useState(false);
    const [isLangToggleOpen, setIsLangToggleOpen] = useState(false); // 언어 선택 팝다운 (우측)

    // Fallback based on props or 'ko'
    const lang = language || 'ko';
    const t = translations[lang] || translations.ko;

    const languages = [
        { code: 'en', label: 'EN' },
        { code: 'jp', label: 'JP' },
        { code: 'es', label: 'ES' },
        { code: 'ko', label: 'KR' }
    ];

    return (
        <nav className="fixed top-0 w-full z-50 bg-gradient-to-b from-black/60 via-black/40 to-transparent backdrop-blur-2xl border-b border-white/5 px-8 md:px-12 py-4 flex justify-between items-center text-white">
            {/* 1. 로고 섹션 */}
            <div
                onClick={() => onMenuClick('home')}
                className="text-xl md:text-2xl font-black cursor-pointer select-none hover:text-transparent hover:bg-gradient-to-r hover:from-purple-400 hover:to-blue-400 hover:bg-clip-text transition-all duration-300"
            >
                {t.brand || t.logo}
            </div>

            {/* 2. 중앙 메뉴 섹션 */}
            <div className="hidden md:flex gap-8 items-center font-medium text-gray-400 text-sm">
                {/* 놀이마당 팝다운 */}
                <div
                    className="relative group cursor-pointer h-full"
                    onMouseEnter={() => setIsPlayMenuOpen(true)}
                    onMouseLeave={() => setIsPlayMenuOpen(false)}
                >
                    <span className="flex items-center gap-1 px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 transition-all duration-300 font-semibold tracking-wide">
                        {t.nav.play} <small className="text-[9px] opacity-40 ml-1">▼</small>
                    </span>

                    <AnimatePresence>
                        {isPlayMenuOpen && (
                            <motion.div
                                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                                animate={{ opacity: 1, y: 0, scale: 1 }}
                                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                                transition={{ duration: 0.2 }}
                                className="absolute top-full left-1/2 -translate-x-1/2 pt-3 w-52 z-50"
                            >
                                <div className="bg-gradient-to-b from-zinc-900/95 to-black/90 backdrop-blur-xl border border-white/10 rounded-2xl overflow-hidden shadow-2xl p-2 flex flex-col gap-1 border-t border-white/20">
                                    <div
                                        onClick={() => { onMenuClick('wordrain'); setIsPlayMenuOpen(false); }}
                                        className="px-4 py-3 hover:bg-gradient-to-r hover:from-purple-500/20 hover:to-transparent rounded-lg text-sm text-gray-300 hover:text-white transition-all cursor-pointer flex items-center gap-3 font-medium"
                                    >
                                        <span className="text-lg">🌧️</span> <span>{t.play_sub1}</span>
                                    </div>
                                    <div
                                        onClick={() => { onMenuClick('match'); setIsPlayMenuOpen(false); }}
                                        className="px-4 py-3 hover:bg-gradient-to-r hover:from-blue-500/20 hover:to-transparent rounded-lg text-sm text-gray-300 hover:text-white transition-all cursor-pointer flex items-center gap-3 font-medium"
                                    >
                                        <span className="text-lg">🎴</span> <span>{t.play_sub2}</span>
                                    </div>
                                    <div
                                        onClick={() => { onMenuClick('quiz'); setIsPlayMenuOpen(false); }}
                                        className="px-4 py-3 hover:bg-gradient-to-r hover:from-cyan-500/20 hover:to-transparent rounded-lg text-sm text-gray-300 hover:text-white transition-all cursor-pointer flex items-center gap-3 font-medium"
                                    >
                                        <span className="text-lg">🧩</span> <span>Quiz</span>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                <div onClick={() => onMenuClick('market')} className="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 cursor-pointer transition-all duration-300 font-semibold tracking-wide">
                    {t.nav.market}
                </div>
                <div onClick={() => onMenuClick('rankings')} className="px-3 py-2 rounded-lg hover:text-white hover:bg-white/5 cursor-pointer transition-all duration-300 font-semibold tracking-wide">
                    {t.nav.ranking}
                </div>
            </div>

            {/* 3. 우측 유틸리티 섹션 */}
            <div className="flex items-center gap-3 md:gap-4">
                {/* 언어 선택 팝다운 메뉴 */}
                <div className="relative">
                    <button
                        onClick={() => setIsLangToggleOpen(!isLangToggleOpen)}
                        className="hidden sm:flex items-center gap-2 bg-white/[0.08] backdrop-blur-sm px-4 py-2 rounded-full border border-white/20 text-xs font-bold hover:bg-white/15 hover:border-white/30 transition-all duration-300 text-gray-300 hover:text-white"
                    >
                        🌐 <span className="font-semibold">{lang.toUpperCase()}</span>
                    </button>

                    {isLangToggleOpen && (
                        <motion.div 
                            initial={{ opacity: 0, y: -10, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: -10, scale: 0.95 }}
                            transition={{ duration: 0.15 }}
                            className="absolute top-full right-0 mt-2 bg-gradient-to-b from-zinc-900/95 to-black/90 backdrop-blur-xl border border-white/20 rounded-2xl overflow-hidden shadow-2xl z-[60] w-36"
                        >
                            {languages.map((l) => (
                                <button
                                    key={l.code}
                                    onClick={() => { setLanguage(l.code); setIsLangToggleOpen(false); }}
                                    className={`block w-full px-6 py-3 text-left text-sm hover:bg-white/10 transition-all font-semibold ${lang === l.code ? 'text-purple-400 bg-white/5 border-l-2 border-purple-500' : 'text-gray-400 hover:text-white'}`}
                                >
                                    {l.label}
                                </button>
                            ))}
                        </motion.div>
                    )}
                </div>

                {/* 테마 모드 토글 */}
                <button
                    onClick={() => setIsDark(!isDark)}
                    className="p-2 text-lg hover:bg-white/10 rounded-lg transition-all duration-300 hover:scale-110"
                    title="Toggle Theme"
                >
                    {isDark ? '☀️' : '🌙'}
                </button>

                {/* 계정/로그인 버튼 */}
                <button
                    onClick={() => {
                        if (user) onMenuClick('account');
                        else onAuthClick();
                    }}
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-lg font-bold transition-all duration-300 hover:scale-110 active:scale-95 ${
                        user 
                            ? 'bg-gradient-to-br from-purple-500 to-blue-500 shadow-lg shadow-purple-500/30 hover:shadow-purple-500/50' 
                            : 'bg-white/10 border border-white/20 hover:bg-white/20 hover:border-white/30'
                    }`}
                    title={user ? "My Account" : "Login / Sign Up"}
                >
                    {user ? '👤' : '🔑'}
                </button>
            </div>
        </nav>
    );
}